import { QUESTION_TYPE } from '../dynamic-form/dynamic-form.validation';
import {
  TableInputColumn,
  TableInputRowData,
  TableInputDataByPage,
  TableInputDelete,
  TableInputUpdate,
} from './tableInput.types';
import { stateAndRegionOptions, stateCodesByName, stateNamesByCode } from '@utils/enums';
import { useTableInput, useAquaticResources } from '@src/stores';
import { QuestionValue } from '../dynamic-form/types';
import type { ArChanges, Geometry } from '@api/services/aquatic-resources/types';

export const MEASUREMENT_UNIT_AREA_OPTIONS = [
  { value: 'ACRE', label: 'Acre', type: 'Area' },
  { value: 'SQ_FT', label: 'Sq Ft.', type: 'Area' },
];

export const MEASUREMENT_UNIT_LINEAR_OPTIONS = [{ value: 'FOOT', label: 'Foot', type: 'Linear' }];

export const MEASUREMENT_UNIT_OPTIONS = [...MEASUREMENT_UNIT_AREA_OPTIONS, ...MEASUREMENT_UNIT_LINEAR_OPTIONS];

export const MEASUREMENT_TYPE_OPTIONS = [
  { label: 'Area', value: 'Area', sortOrder: 1 },
  { label: 'Linear', value: 'Linear', sortOrder: 2 },
];

export const AR_PROP_COL_IDX = {
  waterName: 0,
  state: 1,
  latitude: 2,
  longitude: 3,
  measurementType: 4,
  measurementAmount: 5,
  measurementUnit: 6,
  localWaterway: 7,
  cowardinCode: 8,
  hgmCode: 9,
  jurisdictionalDeterminationType: 10,
  aquaticResourceType: 11,
  regulationType: 12,
} as const;

export const AR_COL_IDX_PROP = Object.fromEntries(Object.entries(AR_PROP_COL_IDX).map(([key, value]) => [value, key]));

export const arTableInputColumnData: TableInputColumn[] = [
  {
    columnLabel: 'Water Name/Label',
    columnNumber: 1,
    isRequired: true,
    columnId: AR_PROP_COL_IDX.waterName,
    questionTypeId: QUESTION_TYPE.shortAnswer,
    width: 180,
  },
  {
    columnLabel: 'State',
    columnNumber: 2,
    isRequired: true,
    columnId: AR_PROP_COL_IDX.state,
    questionTypeId: QUESTION_TYPE.option,
    options: stateAndRegionOptions,
    width: 180,
  },
  {
    columnLabel: 'Latitude',
    columnNumber: 3,
    isRequired: true,
    columnId: AR_PROP_COL_IDX.latitude,
    questionTypeId: QUESTION_TYPE.shortAnswer,
    width: 100,
  },
  {
    columnLabel: 'Longitude',
    columnNumber: 4,
    isRequired: true,
    columnId: AR_PROP_COL_IDX.longitude,
    questionTypeId: QUESTION_TYPE.shortAnswer,
    width: 100,
  },
  {
    columnLabel: 'Measurement Type',
    columnNumber: 5,
    isRequired: true,
    options: MEASUREMENT_TYPE_OPTIONS,
    columnId: AR_PROP_COL_IDX.measurementType,
    questionTypeId: QUESTION_TYPE.option,
    width: 150,
  },
  {
    columnLabel: 'Measurement Amount',
    columnNumber: 6,
    isRequired: true,
    columnId: AR_PROP_COL_IDX.measurementAmount,
    questionTypeId: QUESTION_TYPE.number,
    width: 170,
  },
  {
    columnLabel: 'Measurement Unit',
    columnNumber: 7,
    isRequired: true,
    options: [...MEASUREMENT_UNIT_OPTIONS],
    columnId: AR_PROP_COL_IDX.measurementUnit,
    questionTypeId: QUESTION_TYPE.option,
    width: 140,
  },
  {
    columnLabel: 'Local Waterway',
    columnNumber: 8,
    isRequired: false,
    columnId: AR_PROP_COL_IDX.localWaterway,
    questionTypeId: QUESTION_TYPE.shortAnswer,
    width: 120,
  },
  {
    columnLabel: 'Cowardin Code',
    columnNumber: 9,
    isRequired: false,
    columnId: AR_PROP_COL_IDX.cowardinCode,
    questionTypeId: QUESTION_TYPE.option,
    options: [], // options set dynamically from lookup table data
    width: 120,
  },
  {
    columnLabel: 'HGM Code',
    columnNumber: 10,
    isRequired: false,
    columnId: AR_PROP_COL_IDX.hgmCode,
    questionTypeId: QUESTION_TYPE.option,
    options: [], // options set dynamically from lookup table data
    width: 140,
  },
  {
    columnLabel: 'JD Type',
    columnNumber: 11,
    isRequired: false,
    columnId: AR_PROP_COL_IDX.jurisdictionalDeterminationType,
    questionTypeId: QUESTION_TYPE.option,
    options: [
      { value: 'Approved', label: 'Approved' },
      { value: 'No JD Requested', label: 'No JD Requested' },
      { value: 'Preliminary', label: 'Preliminary' },
    ],
    width: 160,
  },
  {
    columnLabel: 'Aquatic Resource Type',
    columnNumber: 12,
    isRequired: false,
    columnId: AR_PROP_COL_IDX.aquaticResourceType,
    questionTypeId: QUESTION_TYPE.option,
    options: [], // options set dynamically
    width: 200,
  },
];

// AR API data -> table input row data
// page number and per page could be pulled from the store, but passing them in allows us to refresh the data if these values change
export const convertArApiDataToTableInputData = (
  apiData: AquaticResourceData[],
  pageNumber: number
): TableInputDataByPage => {
  if (!apiData || apiData.length === 0) {
    return { [pageNumber]: { rowData: [] } };
  }

  const rowData = apiData.reduce((acc: TableInputRowData[], rowData, rowIndex) => {
    const tableInputRow: TableInputRowData = Array(Object.keys(AR_PROP_COL_IDX).length).fill({});

    Object.entries(AR_PROP_COL_IDX).forEach(([key, columnNumber]) => {
      tableInputRow[columnNumber] = {
        columnId: AR_PROP_COL_IDX[key as keyof typeof AR_PROP_COL_IDX],
        recordId: rowData.aquaticResourceID,
        rowNumber: rowIndex + 1,
        typeId: AR_PROP_COL_IDX[key as keyof typeof AR_PROP_COL_IDX],
        value: rowData[key as keyof AquaticResourceData] as QuestionValue,
      };
    });

    acc.push(tableInputRow);

    return acc;
  }, []);

  return { [pageNumber]: { rowData } };
};

// AR table input row data -> AR API data
export const convertArTableInputDataToApiData = (requestId: number, version: number): ArChanges => {
  const {
    getTableInputData,
    getTableInputDeleteExclusions,
    getTableInputDeletes,
    getTableInputSelectAllMode,
    getTableInputUpdates,
  } = useTableInput.getState();
  const { getAquaticResourceGeometries } = useAquaticResources.getState();

  const arTableId = `aquatic-resources-table-${requestId}`;
  const tableInputDataByPage = getTableInputData(arTableId)?.pagedData || {};
  const tableDeletes = getTableInputDeletes(arTableId);
  const tableInputDeleteExclusions = getTableInputDeleteExclusions(arTableId);
  const tableUpdates = getTableInputUpdates(arTableId);
  const tableInputAnswerData = Object.values(tableInputDataByPage).flatMap((pageData) => pageData.rowData);
  const isInSelectAllMode = getTableInputSelectAllMode(arTableId);
  const arGeometries = getAquaticResourceGeometries();

  const addedArs: AquaticResourceData[] = [];
  const updatedArs: AquaticResourceData[] = [];
  const deletedArs: AquaticResourceData[] = [];
  const unsavedDeletedArs: (number | string)[] = [];
  const deleteAllArExclusions: AquaticResourceData[] = [];

  tableInputAnswerData.forEach((row) => {
    const recordId = row?.[0]?.recordId;
    const rowUpdates = tableUpdates?.[recordId] || {};

    const isRowDeleted = tableDeletes?.[recordId];
    const isRowExcluded = tableInputDeleteExclusions?.[recordId];
    const isRowUpdated = Object.keys(rowUpdates).length > 0;
    const isRowNew = String(recordId).startsWith('new-record-');

    // Set AR values - use updated value if it exists, otherwise use the original value from the row
    const arId = isRowNew ? 0 : (Number(recordId) as number); // recordId corresponds to aquaticResourceID;
    const arType =
      rowUpdates?.[AR_PROP_COL_IDX.aquaticResourceType] ?? row[AR_PROP_COL_IDX.aquaticResourceType].value ?? null;
    const cowardinCode = rowUpdates?.[AR_PROP_COL_IDX.cowardinCode] ?? row[AR_PROP_COL_IDX.cowardinCode].value ?? null;
    const hgmCode = rowUpdates?.[AR_PROP_COL_IDX.hgmCode] ?? row[AR_PROP_COL_IDX.hgmCode].value ?? null;
    const jdType =
      rowUpdates?.[AR_PROP_COL_IDX.jurisdictionalDeterminationType] ??
      row[AR_PROP_COL_IDX.jurisdictionalDeterminationType].value ??
      null;
    const latitude = rowUpdates?.[AR_PROP_COL_IDX.latitude] ?? row[AR_PROP_COL_IDX.latitude].value ?? 0;
    const localWaterway =
      rowUpdates?.[AR_PROP_COL_IDX.localWaterway] ?? row[AR_PROP_COL_IDX.localWaterway].value ?? null;
    const longitude = rowUpdates?.[AR_PROP_COL_IDX.longitude] ?? row[AR_PROP_COL_IDX.longitude].value ?? 0;
    const geometry: Geometry = arGeometries[recordId] ?? {
      type: 'Point',
      coordinates: [Number(longitude), Number(latitude)],
    };
    const measurementAmount =
      rowUpdates?.[AR_PROP_COL_IDX.measurementAmount] ?? row[AR_PROP_COL_IDX.measurementAmount].value ?? 0;
    const measurementType =
      rowUpdates?.[AR_PROP_COL_IDX.measurementType] ?? row[AR_PROP_COL_IDX.measurementType].value ?? '';
    const measurementUnit =
      rowUpdates?.[AR_PROP_COL_IDX.measurementUnit] ?? row[AR_PROP_COL_IDX.measurementUnit].value ?? '';
    const state = rowUpdates?.[AR_PROP_COL_IDX.state] ?? row[AR_PROP_COL_IDX.state].value ?? '';
    const regulationType =
      rowUpdates?.[AR_PROP_COL_IDX.regulationType] ?? row[AR_PROP_COL_IDX.regulationType]?.value ?? '';
    const waterName = rowUpdates?.[AR_PROP_COL_IDX.waterName] ?? row[AR_PROP_COL_IDX.waterName].value ?? '';

    const arRecord: AquaticResourceData = {
      aquaticResourceID: arId,
      aquaticResourceType: arType?.toString() || null,
      cowardinCode: cowardinCode?.toString() || null,
      geometry,
      hgmCode: hgmCode?.toString() || null,
      jurisdictionalDeterminationType: jdType?.toString() || '',
      latitude: Number(latitude),
      localWaterway: localWaterway?.toString() || null,
      longitude: Number(longitude),
      measurementAmount: Number(measurementAmount),
      measurementType: String(measurementType) as AquaticResourceData['measurementType'],
      measurementUnit: String(measurementUnit) as AquaticResourceData['measurementUnit'],
      regulationType: String(regulationType),
      requestID: requestId,
      state: String(state),
      version,
      waterName: String(waterName),
    };

    if ((isRowNew && !isRowDeleted) || (isRowNew && isRowExcluded)) {
      addedArs.push(arRecord);
    } else if (!isInSelectAllMode && !isRowNew && isRowDeleted) {
      deletedArs.push(arRecord);
    } else if (isRowNew && isRowDeleted) {
      unsavedDeletedArs.push(recordId ?? '');
    } else if (isInSelectAllMode && isRowExcluded) {
      deleteAllArExclusions.push(arRecord);
    } else if (isRowUpdated) {
      updatedArs.push(arRecord);
    }
  });

  return {
    addedArs,
    deleteAllArExclusions,
    deletedArs,
    unsavedDeletedArs,
    updatedArs,
  };
};

export type AquaticResourceData = {
  requestID: number;
  version: number;
  aquaticResourceID: number;
  jurisdictionalDeterminationType: string;
  waterName: string;
  state: string;
  latitude: number;
  longitude: number;
  geometry: Geometry;
  measurementType: 'Area' | 'Length' | 'Volume';
  measurementUnit: 'ACRE' | 'FEET' | 'CUBIC_FEET';
  measurementAmount: number;
  hgmCode: string | null;
  cowardinCode: string | null;
  regulationType: string;
  aquaticResourceType: string | null;
  localWaterway: string | null;
};

export const buildAquaticResourceRow = (rowData: TableInputRowData) => ({
  aquaticResourceID: rowData[0]?.recordId,
  waterName: rowData[AR_PROP_COL_IDX.waterName]?.value,
  state: rowData[AR_PROP_COL_IDX.state]?.value,
  latitude: rowData[AR_PROP_COL_IDX.latitude]?.value,
  longitude: rowData[AR_PROP_COL_IDX.longitude]?.value,
  measurementType: rowData[AR_PROP_COL_IDX.measurementType]?.value,
  measurementAmount: rowData[AR_PROP_COL_IDX.measurementAmount]?.value,
  measurementUnit: rowData[AR_PROP_COL_IDX.measurementUnit]?.value,
  localWaterway: rowData[AR_PROP_COL_IDX.localWaterway]?.value,
  cowardinCode: rowData[AR_PROP_COL_IDX.cowardinCode]?.value,
  hgmCode: rowData[AR_PROP_COL_IDX.hgmCode]?.value,
  jurisdictionalDeterminationType: rowData[AR_PROP_COL_IDX.jurisdictionalDeterminationType]?.value,
  aquaticResourceType: rowData[AR_PROP_COL_IDX.aquaticResourceType]?.value,
  rowIssues: null,
});

export const getWaterNameCounts = (allRows: TableInputRowData[]): Map<string, number> => {
  const waterNameCounts = new Map<string, number>();

  allRows.forEach((rowData) => {
    const waterName = rowData[AR_PROP_COL_IDX.waterName]?.value;

    if (typeof waterName !== 'string') {
      return;
    }

    const normalizedWaterName = waterName.trim().toUpperCase();

    if (normalizedWaterName.length === 0) {
      return;
    }

    const currentNum = waterNameCounts.get(normalizedWaterName) || 0;
    waterNameCounts.set(normalizedWaterName, currentNum + 1);
  });

  return waterNameCounts;
};

export const getResolvedDuplicateWaterNameIds = ({
  allRows,
  deletedRecordId,
  tableDeletes,
  tableUpdates,
}: {
  allRows: TableInputRowData[];
  deletedRecordId: number | string;
  tableDeletes: TableInputDelete;
  tableUpdates: TableInputUpdate;
}): number[] => {
  const getEffectiveWaterName = (rowData: TableInputRowData) => {
    const recordId = rowData[0]?.recordId;
    const updatedValue = tableUpdates?.[recordId]?.[AR_PROP_COL_IDX.waterName];
    const value = updatedValue !== undefined ? updatedValue : rowData[AR_PROP_COL_IDX.waterName]?.value;
    return typeof value === 'string' ? value.trim().toUpperCase() : '';
  };
  const deletedRow = allRows.find((rowData) => String(rowData[0]?.recordId) === String(deletedRecordId));
  const idsToClear: number[] = [];
  const numericDeletedRecordId = Number(deletedRecordId);
  if (Number.isFinite(numericDeletedRecordId)) {
    idsToClear.push(numericDeletedRecordId);
  }
  if (!deletedRow) {
    return idsToClear;
  }
  const deletedWaterName = getEffectiveWaterName(deletedRow);
  if (!deletedWaterName) {
    return idsToClear;
  }
  const remainingRowsWithSameName = allRows.filter((rowData) => {
    const recordId = rowData[0]?.recordId;
    const isDeleted =
      String(recordId) === String(deletedRecordId) || tableDeletes?.[recordId] || rowData[0]?.status === 'deleted';
    if (isDeleted) {
      return false;
    }
    return getEffectiveWaterName(rowData) === deletedWaterName;
  });
  if (remainingRowsWithSameName.length !== 1) {
    return idsToClear;
  }
  const remainingRecordId = Number(remainingRowsWithSameName[0][0]?.recordId);
  if (Number.isFinite(remainingRecordId)) {
    idsToClear.push(remainingRecordId);
  }
  return idsToClear;
};

export const getResolvedDuplicateWaterNameIdsAfterUpdate = ({
  allRows,
  updatedRecordId,
  tableDeletes,
  tableUpdates,
}: {
  allRows: TableInputRowData[];
  updatedRecordId: number | string;
  tableDeletes: TableInputDelete;
  tableUpdates: TableInputUpdate;
}): number[] => {
  const updatedRow = allRows.find((rowData) => String(rowData[0]?.recordId) === String(updatedRecordId));
  if (!updatedRow) {
    return [];
  }
  const originalWaterName = updatedRow[AR_PROP_COL_IDX.waterName]?.value;
  const normalizedOriginalWaterName =
    typeof originalWaterName === 'string' ? originalWaterName.trim().toUpperCase() : '';
  const idsToClear: number[] = [];
  const numericUpdatedRecordId = Number(updatedRecordId);
  // api duplicate flag for the row that was edited is stale
  if (Number.isFinite(numericUpdatedRecordId)) {
    idsToClear.push(numericUpdatedRecordId);
  }
  if (!normalizedOriginalWaterName) {
    return idsToClear;
  }
  const remainingRowsWithOriginalName = allRows.filter((rowData) => {
    const recordId = rowData[0]?.recordId;
    if (tableDeletes?.[recordId] || String(recordId) === String(updatedRecordId)) {
      return false;
    }
    const updatedValue = tableUpdates?.[recordId]?.[AR_PROP_COL_IDX.waterName];
    const value = updatedValue !== undefined ? updatedValue : rowData[AR_PROP_COL_IDX.waterName]?.value;
    return typeof value === 'string' && value.trim().toUpperCase() === normalizedOriginalWaterName;
  });
  // If only one of the original duplicates remains, its api duplicate flag is stale too.
  if (remainingRowsWithOriginalName.length === 1) {
    const remainingId = Number(remainingRowsWithOriginalName[0][0]?.recordId);

    if (Number.isFinite(remainingId)) {
      idsToClear.push(remainingId);
    }
  }
  return idsToClear;
};
